import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { Session } from '@supabase/supabase-js'
import { getWillieSupabaseClient } from '~/utils/supabase'
import { WillieSupabaseClient } from '@willie/supabase'

const supabase = getWillieSupabaseClient()

export interface AuthContext {
  session: Session | null
  logout: () => Promise<void>
  supabaseClient: WillieSupabaseClient
}

const AuthContext = createContext<AuthContext>({
  session: null,
  logout: async () => {},
  supabaseClient: supabase
})

type AuthProviderProps = {
  children: ReactNode
}

const AuthProvider = ({ children }: AuthProviderProps) => {
  const [session, setSession] = useState<Session | null>(null)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
    })

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession((prevSession) => {
        if (prevSession?.access_token !== newSession?.access_token) {
          return newSession
        }
        return prevSession
      })
    })

    return () => subscription.unsubscribe()
  }, [])

  const logout = async () => {
    await supabase.auth.signOut()
    setSession(null)
  }

  return (
    <AuthContext.Provider value={{ session, logout, supabaseClient: supabase }}>{children}</AuthContext.Provider>
  )
}

const useAuth = () => useContext(AuthContext)

export { AuthProvider, useAuth }
